import { useState } from "react";
import Alert from "@amzn/meridian/esm/components/alert";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { THEME_COLOR } from "../../styles/colors";
import {
  EXTERNAL_PARTICIPANTS_VALUE,
  orgColor,
  WHOS_WHO_STATE,
} from "./whosWho.constants";
import {
  buildWhosWhoSections,
  formatWhosWhoCountLabel,
} from "./whosWho.utils";
import InviteeCard from "./InviteeCard";
import LegacyWhosWhoPanel from "./legacy/WhosWhoPanel";
import SkeletonCard from "./SkeletonCard";
import WhosWhoOrgPills from "./WhosWhoOrgPills";
import WhosWhoOrgSelect from "./WhosWhoOrgSelect";

const MAX_ORG_PILLS = 5;

const accentFor = (person, orgColorMap) => orgColorMap[person.org] || THEME_COLOR.MIST_GRAY;

const filterInvitees = (invitees, activeFilter) => {
  if (activeFilter === EXTERNAL_PARTICIPANTS_VALUE) {
    return invitees.filter((person) => person.external);
  }
  if (activeFilter) {
    return invitees.filter((person) => !person.external && person.org === activeFilter);
  }
  return invitees;
};

const formatParticipantCount = (visibleCount, totalCount, activeFilter) => {
  if (activeFilter) return `${visibleCount} of ${totalCount} participants`;
  return `${totalCount} participants`;
};

const buildOrganizationFilterData = (invitees) => {
  const orgCounts = Object.create(null);
  let externalCount = 0;

  invitees.forEach((person) => {
    if (person.external) {
      externalCount += 1;
    } else if (person.org) {
      orgCounts[person.org] = (orgCounts[person.org] ?? 0) + 1;
    }
  });

  const orgs = Object.keys(orgCounts)
    .sort((left, right) => left.localeCompare(right));
  const orgColorMap = Object.create(null);
  orgs.forEach((org, index) => {
    orgColorMap[org] = orgColor(index);
  });

  return {
    orgCounts,
    externalCount,
    orgs,
    orgColorMap,
  };
};

const WhosWhoPanel = (props) => {
  const { state, invitees } = props;
  const featureFlags = useFeatureFlags();
  const [activeFilter, setActiveFilter] = useState(null);

  // TODO(MEETEX-299408): Remove the legacy fallback after V2 rollout.
  if (featureFlags?.isWhosWhoV2Enabled !== true) {
    return <LegacyWhosWhoPanel {...props} />;
  }

  const allInvitees = invitees || [];
  const hasInvitees = allInvitees.length > 0;
  const {
    orgCounts,
    externalCount,
    orgs,
    orgColorMap,
  } = buildOrganizationFilterData(allInvitees);
  const visibleInvitees = filterInvitees(allInvitees, activeFilter);
  const isError = state === WHOS_WHO_STATE.ERROR;
  const participantCountLabel = formatParticipantCount(
    visibleInvitees.length,
    allInvitees.length,
    activeFilter
  );

  const renderInvitee = (person) => (
    <InviteeCard
      key={person.id || person.alias || person.email}
      person={person}
      isError={isError}
      accentColor={accentFor(person, orgColorMap)}
    />
  );

  return state === WHOS_WHO_STATE.LOADING ? (
    <Column spacing="300" spacingInset="300 none none none">
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </Column>
  ) : (
    <Column spacing="300" spacingInset="none 100">
      {isError && (
        <Alert type="warning" size="small">
          Some profile details are temporarily unavailable.
        </Alert>
      )}

      {hasInvitees && (
        <>
          <Row spacing="200" alignmentVertical="center" wrap="down">
            <Text type="h100">{participantCountLabel}</Text>
            <Text type="b100" color="secondary">Powered by Aza</Text>
          </Row>

          {orgs.length > MAX_ORG_PILLS ? (
            <WhosWhoOrgSelect
              orgs={orgs}
              orgColorMap={orgColorMap}
              orgCounts={orgCounts}
              externalCount={externalCount}
              totalCount={allInvitees.length}
              activeFilter={activeFilter}
              onSelect={setActiveFilter}
            />
          ) : (
            <WhosWhoOrgPills
              orgs={orgs}
              orgColorMap={orgColorMap}
              externalCount={externalCount}
              activeFilter={activeFilter}
              onSelect={setActiveFilter}
            />
          )}

          {isError
            ? visibleInvitees.map(renderInvitee)
            : buildWhosWhoSections(visibleInvitees)
              .filter((section) => section.invitees.length > 0)
              .map((section) => (
                <Column
                  key={section.id}
                  spacing="450"
                  spacingInset="300 none none none"
                >
                  <Text type="h100">
                    {formatWhosWhoCountLabel(section.label, section.invitees.length)}
                  </Text>
                  <Column spacing="300">
                    {section.invitees.map(renderInvitee)}
                  </Column>
                </Column>
              ))}
        </>
      )}
    </Column>
  );
};

export default WhosWhoPanel;
